import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { 
  Upload, 
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Download,
  ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ImportTab = ({ businessId, token, authHeaders, onImportComplete }) => {
  const { toast } = useToast();
  const [importing, setImporting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dataType, setDataType] = useState('both');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({ title: "Error", description: "Please upload a CSV file", variant: "destructive" });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/api/mybookie/import/csv?business_id=${businessId}&data_type=${dataType}`,
        formData,
        {
          headers: {
            ...authHeaders.headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setImportResult({
        success: true,
        message: response.data.message,
        count: response.data.imported_count,
        errors: response.data.errors
      });

      toast({ title: "Success", description: response.data.message });
      
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Import failed";
      setImportResult({
        success: false,
        message: message,
        count: 0,
        errors: []
      });
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Import Card */}
      <Card className="border-[#E6E8E6]">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#E2ECE7] rounded-sm">
              <FileSpreadsheet className="w-8 h-8 text-[#0A2B1D]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-[#111614] mb-1">Import from QuickBooks</h3>
              <p className="text-sm text-[#5C6661] mb-4">
                Export your data from QuickBooks as a CSV file, then upload it here. 
                We'll automatically import your expenses and income.
              </p>
              
              {/* Data Type Selection */}
              <div className="mb-4">
                <p className="text-sm font-medium text-[#111614] mb-2">What do you want to import?</p>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="dataType"
                      value="both"
                      checked={dataType === 'both'}
                      onChange={(e) => setDataType(e.target.value)}
                      className="text-[#0A2B1D]"
                    />
                    <span className="text-sm text-[#5C6661]">Expenses & Income</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="dataType"
                      value="expenses"
                      checked={dataType === 'expenses'}
                      onChange={(e) => setDataType(e.target.value)}
                      className="text-[#0A2B1D]"
                    />
                    <span className="text-sm text-[#5C6661]">Expenses only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="dataType"
                      value="income"
                      checked={dataType === 'income'}
                      onChange={(e) => setDataType(e.target.value)}
                      className="text-[#0A2B1D]"
                    />
                    <span className="text-sm text-[#5C6661]">Income only</span>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload">
                  <Button 
                    asChild
                    disabled={importing || !businessId}
                    className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm cursor-pointer"
                  >
                    <span>
                      {importing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {importing ? 'Importing...' : 'Upload CSV'}
                    </span>
                  </Button>
                </label>
                <Button 
                  variant="outline"
                  onClick={() => setShowHelp(true)}
                  className="border-[#E6E8E6]"
                >
                  How to export from QuickBooks
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Import Result */}
      {importResult && (
        <Card className={`border-2 ${importResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              {importResult.success ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600" />
              )}
              <div>
                <p className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {importResult.message}
                </p>
                {importResult.success && importResult.count > 0 && (
                  <p className="text-sm text-green-700 mt-1">
                    {importResult.count} records imported successfully
                  </p>
                )}
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-red-700">Errors:</p>
                    <ul className="text-sm text-red-600 list-disc list-inside">
                      {importResult.errors.slice(0, 5).map((err, i) => (
                        <li key={`err-${i}`}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supported Formats */}
      <Card className="border-[#E6E8E6]">
        <CardContent className="p-6">
          <h4 className="font-medium text-[#111614] mb-3">Supported CSV Formats</h4>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-[#5C6661] mb-2">Required columns:</p>
              <ul className="text-[#5C6661] space-y-1">
                <li>• <code className="bg-[#F6F5F2] px-1">Date</code> or <code className="bg-[#F6F5F2] px-1">Transaction Date</code></li>
                <li>• <code className="bg-[#F6F5F2] px-1">Amount</code> or <code className="bg-[#F6F5F2] px-1">Total</code></li>
                <li>• <code className="bg-[#F6F5F2] px-1">Description</code> or <code className="bg-[#F6F5F2] px-1">Memo</code></li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-[#5C6661] mb-2">Optional columns:</p>
              <ul className="text-[#5C6661] space-y-1">
                <li>• <code className="bg-[#F6F5F2] px-1">Category</code> or <code className="bg-[#F6F5F2] px-1">Account</code></li>
                <li>• <code className="bg-[#F6F5F2] px-1">Type</code> or <code className="bg-[#F6F5F2] px-1">Transaction Type</code></li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-[#8A948F] mt-4">
            Negative amounts are treated as expenses. Positive amounts as income. 
            Or use the Type column with "expense" or "income".
          </p>
        </CardContent>
      </Card>

      {/* Help Dialog */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="sm:max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#111614]">Export from QuickBooks</DialogTitle>
            <DialogDescription className="text-[#5C6661]">
              Follow these steps to export your QuickBooks data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#0A2B1D] text-white rounded-sm flex items-center justify-center flex-shrink-0">
                1
              </div>
              <div>
                <p className="font-medium text-[#111614]">Open QuickBooks Online</p>
                <p className="text-sm text-[#5C6661]">Log in to your QuickBooks account</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#0A2B1D] text-white rounded-sm flex items-center justify-center flex-shrink-0">
                2
              </div>
              <div>
                <p className="font-medium text-[#111614]">Go to Reports</p>
                <p className="text-sm text-[#5C6661]">Navigate to Reports → All Reports → Transaction List by Date</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#0A2B1D] text-white rounded-sm flex items-center justify-center flex-shrink-0">
                3
              </div>
              <div>
                <p className="font-medium text-[#111614]">Set date range</p>
                <p className="text-sm text-[#5C6661]">Select the date range you want to export</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#0A2B1D] text-white rounded-sm flex items-center justify-center flex-shrink-0">
                4
              </div>
              <div>
                <p className="font-medium text-[#111614]">Export to CSV</p>
                <p className="text-sm text-[#5C6661]">Click the Export button (↓) and select "Export to CSV"</p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-[#0A2B1D] text-white rounded-sm flex items-center justify-center flex-shrink-0">
                5
              </div>
              <div>
                <p className="font-medium text-[#111614]">Upload to MyBookie</p>
                <p className="text-sm text-[#5C6661]">Return here and upload the CSV file</p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowHelp(false)} className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportTab;
